import { X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {string | null | undefined} iso
 */
function isoToDateInputValue(iso) {
    if (!iso || typeof iso !== 'string') return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onSubmit: (payload: {
 *     id?: string;
 *     firstName: string;
 *     lastName: string;
 *     birthDate: string;
 *   }) => void | Promise<void>;
 *   submitting?: boolean;
 *   mode?: 'add' | 'edit';
 *   initialValues?: {
 *     id?: string;
 *     firstName?: string;
 *     lastName?: string;
 *     birthDate?: string | null;
 *   } | null;
 * }} props
 */
export function AddChildModal({
    open,
    onClose,
    onSubmit,
    submitting = false,
    mode = 'add',
    initialValues = null,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState(
        /** @type {Record<string, string>} */ ({}),
    );
    const isEdit = mode === 'edit';

    useEffect(() => {
        if (!open) {
            setFirstName('');
            setLastName('');
            setBirthDate('');
            setError('');
            setFieldErrors({});
            return;
        }

        if (isEdit && initialValues) {
            setFirstName(String(initialValues.firstName || '').trim());
            setLastName(String(initialValues.lastName || '').trim());
            setBirthDate(isoToDateInputValue(initialValues.birthDate));
            setError('');
            setFieldErrors({});
        }
    }, [open, isEdit, initialValues]);

    useEffect(() => {
        if (!open || Object.keys(fieldErrors).length === 0) return;
        document
            .querySelector('.app-modal .is-invalid')
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [fieldErrors, open]);

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !submitting) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose, submitting]);

    if (!open) return null;

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        const fn = firstName.trim();
        const ln = lastName.trim();
        const nextFieldErrors = /** @type {Record<string, string>} */ ({});
        const requiredMessage = t('profilePage.fieldRequired');

        if (!fn) {
            nextFieldErrors.firstName = requiredMessage;
        }
        if (!ln) {
            nextFieldErrors.lastName = requiredMessage;
        }
        if (!birthDate) {
            nextFieldErrors.birthDate = requiredMessage;
        }

        if (Object.keys(nextFieldErrors).length > 0) {
            setFieldErrors(nextFieldErrors);
            return;
        }

        setFieldErrors({});

        const iso = new Date(`${birthDate}T12:00:00`).toISOString();
        const payload = {
            firstName: fn,
            lastName: ln,
            birthDate: iso,
        };

        if (isEdit && initialValues?.id) {
            payload.id = String(initialValues.id);
        }

        try {
            await onSubmit(payload);
        } catch (err) {
            const msg =
                err && typeof err === 'object' && 'message' in err
                    ? String(/** @type {{ message?: unknown }} */ (err).message)
                    : '';
            setError(
                msg ||
                    (isEdit
                        ? t('profilePage.editChildError')
                        : t('profilePage.addChildError')),
            );
        }
    }

    return (
        <div
            className="app-modal-backdrop"
            onClick={submitting ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="app-modal-header">
                    <h2 id={titleId} className="app-modal-title h6 mb-0">
                        {isEdit
                            ? t('profilePage.editChildTitle')
                            : t('profilePage.addChildTitle')}
                    </h2>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label={t('profilePage.addChildClose')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="app-modal-body">
                        <div className="mb-3">
                            <label
                                htmlFor="add-child-first-name"
                                className="form-label login-field-label"
                            >
                                {t('profilePage.addChildFirstName')}
                                <span className="text-danger ms-1" aria-hidden="true">
                                    *
                                </span>
                            </label>
                            <input
                                id="add-child-first-name"
                                type="text"
                                className={`form-control login-field-input${fieldErrors.firstName ? ' is-invalid' : ''}`}
                                value={firstName}
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    setFieldErrors((prev) => {
                                        if (!prev.firstName) return prev;
                                        const next = { ...prev };
                                        delete next.firstName;
                                        return next;
                                    });
                                }}
                                disabled={submitting}
                                autoComplete="given-name"
                                aria-invalid={fieldErrors.firstName ? 'true' : undefined}
                                aria-describedby={
                                    fieldErrors.firstName
                                        ? 'add-child-first-name-error'
                                        : undefined
                                }
                            />
                            {fieldErrors.firstName ? (
                                <div
                                    id="add-child-first-name-error"
                                    className="invalid-feedback d-block"
                                    role="alert"
                                >
                                    {fieldErrors.firstName}
                                </div>
                            ) : null}
                        </div>
                        <div className="mb-3">
                            <label
                                htmlFor="add-child-last-name"
                                className="form-label login-field-label"
                            >
                                {t('profilePage.addChildLastName')}
                                <span className="text-danger ms-1" aria-hidden="true">
                                    *
                                </span>
                            </label>
                            <input
                                id="add-child-last-name"
                                type="text"
                                className={`form-control login-field-input${fieldErrors.lastName ? ' is-invalid' : ''}`}
                                value={lastName}
                                onChange={(e) => {
                                    setLastName(e.target.value);
                                    setFieldErrors((prev) => {
                                        if (!prev.lastName) return prev;
                                        const next = { ...prev };
                                        delete next.lastName;
                                        return next;
                                    });
                                }}
                                disabled={submitting}
                                autoComplete="family-name"
                                aria-invalid={fieldErrors.lastName ? 'true' : undefined}
                                aria-describedby={
                                    fieldErrors.lastName
                                        ? 'add-child-last-name-error'
                                        : undefined
                                }
                            />
                            {fieldErrors.lastName ? (
                                <div
                                    id="add-child-last-name-error"
                                    className="invalid-feedback d-block"
                                    role="alert"
                                >
                                    {fieldErrors.lastName}
                                </div>
                            ) : null}
                        </div>
                        <div className="mb-0">
                            <label
                                htmlFor="add-child-birth-date"
                                className="form-label login-field-label"
                            >
                                {t('profilePage.addChildBirthDate')}
                                <span className="text-danger ms-1" aria-hidden="true">
                                    *
                                </span>
                            </label>
                            <input
                                id="add-child-birth-date"
                                type="date"
                                className={`form-control login-field-input${fieldErrors.birthDate ? ' is-invalid' : ''}`}
                                value={birthDate}
                                onChange={(e) => {
                                    setBirthDate(e.target.value);
                                    setFieldErrors((prev) => {
                                        if (!prev.birthDate) return prev;
                                        const next = { ...prev };
                                        delete next.birthDate;
                                        return next;
                                    });
                                }}
                                disabled={submitting}
                                aria-invalid={fieldErrors.birthDate ? 'true' : undefined}
                                aria-describedby={
                                    fieldErrors.birthDate
                                        ? 'add-child-birth-date-error'
                                        : undefined
                                }
                            />
                            {fieldErrors.birthDate ? (
                                <div
                                    id="add-child-birth-date-error"
                                    className="invalid-feedback d-block"
                                    role="alert"
                                >
                                    {fieldErrors.birthDate}
                                </div>
                            ) : null}
                        </div>
                        {error ? (
                            <p
                                className="app-modal-error small text-danger mb-0 mt-3"
                                role="alert"
                            >
                                {error}
                            </p>
                        ) : null}
                    </div>
                    <div className="app-modal-footer">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {t('profilePage.addChildCancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting
                                ? isEdit
                                    ? t('profilePage.editChildSubmitting')
                                    : t('profilePage.addChildSubmitting')
                                : isEdit
                                  ? t('profilePage.editChildSubmit')
                                  : t('profilePage.addChildSubmit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
